import React, { useEffect, useId, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { PlusIcon } from '@/assets/icons/icons';
import { CustomDatePicker, CustomInput, CustomSelect } from '@/components';
import {
  askUserFieldError,
  askUserFieldKind,
  askUserInitialValue,
  askUserSteps,
  askUserSubmitValue,
  emptyRow,
  formatAskUserAnswerLines,
  formLayout,
  MAX_RADIO_OPTIONS,
  optionTitle,
  rowLayout,
  sharedUnit,
} from '../chatActions';
import type {
  AskUserAnswers,
  AskUserFieldSchema,
  AskUserRequest,
} from '../types';

// <input type='datetime-local'> speaks "YYYY-MM-DDTHH:mm" in local time with
// no zone, while a `date-time` schema expects a full ISO timestamp -- these
// convert at the input's edge so the value held (and submitted) is always
// the ISO form.
function isoToLocalInput(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localInputToIso(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

const TEXT_INPUT_TYPES: Record<string, string> = {
  date: 'date',
  'date-time': 'datetime-local',
  email: 'email',
  uri: 'url',
};

type FieldProps = {
  schema: AskUserFieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
};

/** One answer input, chosen purely by the schema's shape (see
 * askUserFieldKind) -- every option and label comes from the payload. */
const AskUserField: React.FC<FieldProps> = ({
  schema,
  value,
  onChange,
  label,
}) => {
  const id = useId();
  const kind = askUserFieldKind(schema);

  if (kind === 'single_choice') {
    const options = schema.oneOf ?? [];
    const selectedIndex = options.findIndex((option) => option.const === value);
    if (options.length > MAX_RADIO_OPTIONS) {
      return (
        <select
          className='ai-chat-drawer__ask-select'
          aria-label={label}
          value={selectedIndex >= 0 ? String(selectedIndex) : ''}
          onChange={(event) =>
            onChange(
              event.target.value === ''
                ? undefined
                : options[Number(event.target.value)].const
            )
          }
        >
          <option value='' disabled>
            Select…
          </option>
          {options.map((option, index) => (
            <option key={JSON.stringify(option.const)} value={String(index)}>
              {optionTitle(option)}
            </option>
          ))}
        </select>
      );
    }
    return (
      <div
        className='ai-chat-drawer__ask-options'
        role='radiogroup'
        aria-label={label}
      >
        {options.map((option, index) => (
          <label
            key={JSON.stringify(option.const)}
            className='ai-chat-drawer__ask-option'
          >
            <input
              type='radio'
              name={id}
              checked={selectedIndex === index}
              onChange={() => onChange(option.const)}
            />
            <span>{optionTitle(option)}</span>
          </label>
        ))}
      </div>
    );
  }

  if (kind === 'multi_choice') {
    const options = schema.items?.anyOf ?? [];
    const selected = Array.isArray(value) ? value : [];
    return (
      <div
        className='ai-chat-drawer__ask-options'
        role='group'
        aria-label={label}
      >
        {options.map((option) => {
          const checked = selected.includes(option.const);
          return (
            <label
              key={JSON.stringify(option.const)}
              className='ai-chat-drawer__ask-option ai-chat-drawer__ask-option--multi'
            >
              <input
                type='checkbox'
                checked={checked}
                onChange={() =>
                  // Kept in the schema's option order, not click order, so
                  // the submitted array is stable however it was built.
                  onChange(
                    options
                      .map((item) => item.const)
                      .filter((code) =>
                        code === option.const
                          ? !checked
                          : selected.includes(code)
                      )
                  )
                }
              />
              <span>{optionTitle(option)}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (kind === 'boolean') {
    return (
      <label className='ai-chat-drawer__ask-check'>
        <input
          type='checkbox'
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{schema.title ?? label}</span>
      </label>
    );
  }

  if (kind === 'number') {
    return (
      <input
        className='ai-chat-drawer__ask-input'
        type='number'
        aria-label={label}
        min={schema.minimum}
        max={schema.maximum}
        step={schema.type === 'integer' ? 1 : 'any'}
        value={typeof value === 'number' ? value : ''}
        onChange={(event) =>
          onChange(
            event.target.value === '' ? undefined : Number(event.target.value)
          )
        }
      />
    );
  }

  if (kind === 'rows') {
    return <AskUserRows schema={schema} value={value} onChange={onChange} />;
  }

  if (kind === 'form') {
    return (
      <AskUserForm
        schema={schema}
        value={value}
        label={label}
        onChange={onChange}
      />
    );
  }

  if (schema.format === 'date') {
    // The same picker as the Lane Planner's ship date; the answer stays a plain
    // "YYYY-MM-DD" string, as the browser's own date input gave.
    const picked = typeof value === 'string' && value ? dayjs(value) : null;
    return (
      <CustomDatePicker
        aria-label={label}
        placeholder='Enter date'
        className='custom-picker custom-picker-white ai-chat-drawer__ask-date'
        value={picked?.isValid() ? picked : null}
        onChange={(date) => {
          const day = Array.isArray(date) ? date[0] : date;
          onChange(day ? day.format('YYYY-MM-DD') : '');
        }}
        disabledDate={(current) =>
          !!current && current.isBefore(dayjs(), 'day')
        }
      />
    );
  }

  const inputType =
    (schema.format && TEXT_INPUT_TYPES[schema.format]) || 'text';
  const isDateTime = inputType === 'datetime-local';
  return (
    <input
      className='ai-chat-drawer__ask-input'
      type={inputType}
      aria-label={label}
      minLength={schema.minLength}
      maxLength={schema.maxLength}
      pattern={schema.pattern}
      value={
        isDateTime
          ? isoToLocalInput(value)
          : typeof value === 'string'
            ? value
            : value == null
              ? ''
              : String(value)
      }
      onChange={(event) =>
        onChange(
          isDateTime ? localInputToIso(event.target.value) : event.target.value
        )
      }
    />
  );
};

/** A number typed into a text box: holds what's typed (so "12." survives
 * while typing) and reports the parsed number, or undefined while empty. */
const RowNumberInput: React.FC<{
  value: unknown;
  label: string;
  unit?: string;
  onChange: (value: unknown) => void;
}> = ({ value, label, unit, onChange }) => {
  const [draft, setDraft] = useState(
    typeof value === 'number' ? String(value) : ''
  );
  return (
    <CustomInput
      className='ai-chat-drawer__ask-row-input'
      aria-label={label}
      inputMode='decimal'
      bgColor='var(--surface-container)'
      suffix={unit}
      suffixFontWeight='500'
      suffixColor='var(--text-primary)'
      value={draft}
      onChange={(event) => {
        const text = event.target.value;
        setDraft(text);
        const parsed = Number(text);
        onChange(
          text.trim() === '' || Number.isNaN(parsed) ? undefined : parsed
        );
      }}
    />
  );
};

/** One cell of a package line (or any list entry): a labelled input chosen
 * by the column's schema. Yes/no columns are a tick row instead. */
const AskUserRowCell: React.FC<{
  name: string;
  schema: AskUserFieldSchema;
  value: unknown;
  required: boolean;
  unit?: string;
  onChange: (value: unknown) => void;
}> = ({ name, schema, value, required, unit, onChange }) => {
  const kind = askUserFieldKind(schema);
  const label = `${schema.title ?? name}${required ? ' *' : ''}`;
  if (kind === 'boolean') {
    return (
      <label className='ai-chat-drawer__ask-check'>
        <input
          type='checkbox'
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{schema.title ?? name}</span>
      </label>
    );
  }
  let control: React.ReactNode;
  if (kind === 'single_choice') {
    const options = schema.oneOf ?? [];
    const selected = options.findIndex((option) => option.const === value);
    control = (
      <CustomSelect
        aria-label={label}
        bgColor='var(--surface-container)'
        value={selected >= 0 ? selected : undefined}
        options={options.map((option, index) => ({
          value: index,
          label: optionTitle(option),
        }))}
        onChange={(index) => onChange(options[Number(index)]?.const)}
      />
    );
  } else if (kind === 'number') {
    control = (
      <RowNumberInput
        value={value}
        label={label}
        unit={unit}
        onChange={onChange}
      />
    );
  } else {
    control = (
      <AskUserField
        schema={schema}
        value={value}
        label={label}
        onChange={onChange}
      />
    );
  }
  return (
    <div className='ai-chat-drawer__ask-row-field'>
      <span className='ai-chat-drawer__ask-row-label'>
        {label}
        {schema['x-ui']?.help && schema.description && (
          <span
            className='ai-chat-drawer__ask-help'
            role='img'
            aria-label={schema.description}
            title={schema.description}
          >
            ?
          </span>
        )}
      </span>
      {control}
    </div>
  );
};

/** The cells of one entry, laid out in lines from the schema's `x-ui` hints:
 * fields sharing a row sit side by side, and a unit they share gets its own
 * "Unit" column. Used for a package line and for a group of fields alike. */
const AskUserLines: React.FC<{
  lines: [string, AskUserFieldSchema][][];
  required: Set<string>;
  entry: Record<string, unknown>;
  onCell: (name: string, value: unknown) => void;
}> = ({ lines, required, entry, onCell }) => (
  <>
    {lines.map((line) => {
      const unit = sharedUnit(line);
      return (
        <div
          key={line.map(([name]) => name).join()}
          className='ai-chat-drawer__ask-row-line'
        >
          {line.map(([name, property]) => (
            <AskUserRowCell
              key={name}
              name={name}
              schema={property}
              value={entry[name]}
              required={required.has(name)}
              unit={unit ? undefined : property['x-ui']?.unit}
              onChange={(cell) => onCell(name, cell)}
            />
          ))}
          {unit && (
            <div className='ai-chat-drawer__ask-row-field'>
              <span className='ai-chat-drawer__ask-row-label'>Unit</span>
              <span className='ai-chat-drawer__ask-unit'>{unit}</span>
            </div>
          )}
        </div>
      );
    })}
  </>
);

/** A group of fields (e.g. the trucking cargo) on one screen, laid out like a
 * package line but with nothing to add or remove. */
const AskUserForm: React.FC<FieldProps> = ({
  schema,
  value,
  onChange,
  label,
}) => {
  const record = (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  ) as Record<string, unknown>;
  return (
    <div className='ai-chat-drawer__ask-row' role='group' aria-label={label}>
      <AskUserLines
        lines={formLayout(schema)}
        required={new Set(schema.required ?? [])}
        entry={record}
        onCell={(name, cell) => onChange({ ...record, [name]: cell })}
      />
    </div>
  );
};

/** A list of entries (e.g. package lines), laid out from the schema's
 * `x-ui` hints: columns sharing a row sit side by side, a unit they share
 * gets its own "Unit" column, and each entry can be added or removed. */
const AskUserRows: React.FC<Omit<FieldProps, 'label'>> = ({
  schema,
  value,
  onChange,
}) => {
  const rows = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
  const lines = rowLayout(schema);
  const required = new Set(schema.items?.required ?? []);
  const itemLabel = schema['x-ui']?.itemLabel;
  const canAdd = schema.maxItems === undefined || rows.length < schema.maxItems;
  // A list is never emptied from the panel: at least one entry stays.
  const canRemove = rows.length > Math.max(1, schema.minItems ?? 0);

  return (
    <div className='ai-chat-drawer__ask-rows'>
      {rows.map((row, rowIndex) => (
        <div
          // eslint-disable-next-line react/no-array-index-key -- rows are plain values with no id; every input is controlled from `value`, so a key reused after a removal still renders that row's data
          key={rowIndex}
          className='ai-chat-drawer__ask-row'
          role='group'
          aria-label={`Row ${rowIndex + 1}`}
        >
          <AskUserLines
            lines={lines}
            required={required}
            entry={row}
            onCell={(name, cell) =>
              onChange(
                rows.map((item, index) =>
                  index === rowIndex ? { ...item, [name]: cell } : item
                )
              )
            }
          />
          {canRemove && (
            <button
              type='button'
              className='ai-chat-drawer__ask-link'
              onClick={() =>
                onChange(rows.filter((_, index) => index !== rowIndex))
              }
              aria-label={`Remove row ${rowIndex + 1}`}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {canAdd && (
        <button
          type='button'
          className='ai-chat-drawer__ask-add'
          onClick={() => onChange([...rows, emptyRow(schema)])}
        >
          <PlusIcon width={12} height={12} fill='currentColor' />
          {itemLabel ? `Add ${itemLabel}` : 'Add another'}
        </button>
      )}
    </div>
  );
};

type AIChatQuestionPanelProps = {
  request: AskUserRequest;
  onSubmit: (content: Record<string, unknown>) => void;
};

/** The stepped "Question" panel shown at the end of the thread for an open
 * `ask_user` request: one question per step, every answer submitted
 * together on the final Continue. Generic over whatever action raised it.
 * Mount with `key={request.id}` so a new request starts from its own
 * defaults. */
const AIChatQuestionPanel: React.FC<AIChatQuestionPanelProps> = ({
  request,
  onSubmit,
}) => {
  const [step, setStep] = useState(0);
  const panelRef = useRef<HTMLElement>(null);
  // The panel is the last thing in the scrolling thread, pushed to its bottom
  // edge (see the CSS) so it sits just above the typing box. When it opens, or
  // moves to its next step, scroll the thread to the bottom to lock it there;
  // a form taller than the thread can't be both at the bottom and fully visible,
  // so it opens at its top and settles at the bottom as the user scrolls down.
  useEffect(() => {
    const panel = panelRef.current;
    const thread = panel?.parentElement;
    if (!panel || !thread) return;
    if (panel.offsetHeight <= thread.clientHeight) {
      if (typeof thread.scrollTo === 'function') {
        thread.scrollTo({ top: thread.scrollHeight, behavior: 'smooth' });
      } else {
        thread.scrollTop = thread.scrollHeight;
      }
    } else {
      panel.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  }, [step]);
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      request.questions.map((question) => [
        question.key,
        askUserInitialValue(question.schema),
      ])
    )
  );
  const steps = askUserSteps(request.questions);
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const error =
    current.questions
      .map((question) =>
        askUserFieldError(question.schema, values[question.key])
      )
      .find(Boolean) ?? null;
  const heading =
    current.title ??
    (current.questions[0].question || current.questions[0].header);
  const fields = current.questions.map((question) => (
    <AskUserField
      key={question.key}
      schema={question.schema}
      value={values[question.key]}
      label={question.question || question.header}
      onChange={(value) =>
        setValues((prev) => ({ ...prev, [question.key]: value }))
      }
    />
  ));

  const handleContinue = () => {
    if (error) return;
    if (!isLast) {
      setStep(step + 1);
      return;
    }
    onSubmit(
      Object.fromEntries(
        Object.entries(values)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => {
            const question = request.questions.find((q) => q.key === key);
            return [
              key,
              question ? askUserSubmitValue(question.schema, value) : value,
            ];
          })
      )
    );
  };

  return (
    <section
      className='ai-chat-drawer__ask-panel'
      aria-label='Question'
      ref={panelRef}
    >
      <div className='ai-chat-drawer__ask-body'>
        <div className='ai-chat-drawer__ask-heading'>
          {/* No dismiss control (Figma 44238:73458): typing a message in the
              composer instead is how a user moves on without answering. */}
          <div className='ai-chat-drawer__ask-header'>
            <span className='ai-chat-drawer__ask-title'>Question</span>
          </div>
          <p className='ai-chat-drawer__ask-question'>{heading}</p>
        </div>
        {current.title ? (
          <fieldset
            className='ai-chat-drawer__ask-group ai-chat-drawer__ask-options'
            aria-label={current.title}
          >
            {fields}
          </fieldset>
        ) : (
          fields
        )}
      </div>
      <div className='ai-chat-drawer__ask-footer'>
        <span className='ai-chat-drawer__ask-counter'>
          {step + 1} / {steps.length}
        </span>
        {error && <span className='ai-chat-drawer__ask-error'>{error}</span>}
        <button
          type='button'
          className='ai-chat-drawer__ask-continue'
          onClick={handleContinue}
          disabled={Boolean(error)}
        >
          Continue
        </button>
      </div>
    </section>
  );
};

type AIChatAskUserSummaryProps = {
  request: AskUserRequest;
  answers?: AskUserAnswers;
};

/** A past `ask_user` request, read-only in the thread (Figma 44242:80130):
 * each question in bold, with the answer a later user turn gave in a
 * right-aligned bubble. A request that was never answered (or was cancelled by
 * typing a message instead) is one quiet line, not a "Not answered" per
 * question. */
export const AIChatAskUserSummary: React.FC<AIChatAskUserSummaryProps> = ({
  request,
  answers,
}) => {
  const content = answers?.action === 'accept' ? answers.content : undefined;
  if (content === undefined) {
    return (
      <section
        className='ai-chat-drawer__ask-summary'
        aria-label='Past question'
      >
        <p className='ai-chat-drawer__ask-skipped'>
          {request.questions.length === 1
            ? "You didn't answer this question."
            : "You didn't answer these questions."}
        </p>
      </section>
    );
  }
  return (
    <section className='ai-chat-drawer__ask-summary' aria-label='Past question'>
      {request.questions.map((question) => {
        const answered = content !== undefined && question.key in content;
        return (
          <div key={question.key} className='ai-chat-drawer__ask-answered'>
            <p className='ai-chat-drawer__ask-answered-question'>
              {question.question || question.header}
            </p>
            <p
              className={`ai-chat-drawer__ask-answered-bubble${
                answered ? '' : ' is-unanswered'
              }`}
            >
              {answered
                ? formatAskUserAnswerLines(
                    question.schema,
                    content[question.key]
                  ).join('\n')
                : 'Not answered'}
            </p>
          </div>
        );
      })}
    </section>
  );
};

export default AIChatQuestionPanel;
